"use client";

import React, { useState } from "react";
import { PhoneCall, Mail, MessageSquare, Copy, Check, Loader } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const inputClasses = "flex h-10 w-full rounded-md border border-input bg-card/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all";
const textareaClasses = "flex min-h-[120px] w-full rounded-md border border-input bg-card/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all";
function SupportForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary/10 border border-primary/20 rounded-lg text-center space-y-4">
                <div className="mx-auto size-12 bg-primary/20 rounded-lg flex items-center justify-center mb-4">
                    <Check className="size-6 text-primary" />
                </div>
                <h3 className="text-xl font-default font-semibold text-foreground">Consultation Requested</h3>
                <p className="text-muted-foreground">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary/10">
                    Send Another Message
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-medium">Full Name</FormLabel>
                            <FormControl>
                                <input type="text" placeholder="Alexander Stone" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">Enter your legal or business name</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-medium">Contact Number</FormLabel>
                            <FormControl>
                                <input type="tel" placeholder="1-888-322-2844" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">Best number for our consultants to reach you</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-medium">Business Email</FormLabel>
                            <FormControl>
                                <input type="email" placeholder="ls@lakestoneenterprise.net" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">For secure funding documentation</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="vehicle_reg" rules={{
        required: "Credit goal is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-medium">Funding Goal</FormLabel>
                            <FormControl>
                                <input type="text" placeholder="$50,000 - $250,000" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">Target capital for your business expansion</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="location" rules={{
        required: "Location is required"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="text-foreground font-medium">Business Location</FormLabel>
                            <FormControl>
                                <input type="text" placeholder="City, State" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">Where your enterprise is currently registered</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="issue" rules={{
        required: "Please describe your needs"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="text-foreground font-medium">Consultation Details</FormLabel>
                            <FormControl>
                                <textarea placeholder="Tell us about your credit building or business management needs..." className={textareaClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">Provide details to help us prepare your strategy</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="preferred_contact" rules={{
        required: "Please select a preferred contact method"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2 space-y-3">
                            <FormLabel className="text-foreground font-medium">Preferred Contact Method</FormLabel>
                            <FormControl>
                                <div className="flex gap-6">
                                    {["Phone", "Email"].map((option, i) => <label key={option} data-index={i} className="flex items-center space-x-2 cursor-pointer">
                                            <input type="radio" value={option} checked={field.value === option} onChange={field.onChange} className="size-4 border-primary text-primary focus:ring-primary bg-background" />
                                            <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-foreground">{option}</span>
                                        </label>)}
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <p className="text-destructive text-sm font-medium">{submitError}</p>
                </div>}

            <div className="mt-8">
                <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 font-default font-bold uppercase tracking-wider transition-all hover:-translate-y-1">
                    {isSubmitting ? <>
                            <Loader className="animate-spin mr-2 size-5" />
                            Processing...
                        </> : 'Send Message'}
                </Button>
            </div>
        </Form>;
}
export default function DirectSupport() {
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const phone = "1-888-322-2844";
  const email = "ls@lakestoneenterprise.net";
  const handleCopy = (text: string, type: 'phone' | 'email') => {
    navigator.clipboard.writeText(text);
    if (type === 'phone') {
      setCopiedPhone(true);
      setTimeout(() => setCopiedPhone(false), 2000);
    } else {
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    }
  };
  return <section data-section-id="246984" id="reach-out-to-our-experts" className="bg-background text-foreground py-24 px-4 md:px-8 lg:px-12 relative overflow-hidden">
            {}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#10b98108_1px,transparent_1px),linear-gradient(to_bottom,#10b98108_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none"></div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-primary/10 blur-[160px] rounded-lg pointer-events-none"></div>

            <div className="max-w-7xl mx-auto relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="text-4xl md:text-6xl font-bold font-default tracking-tighter mb-4 text-foreground">
                        Reach Out to Our Experts
                    </h2>
                    <p className="text-xl text-muted-foreground font-default">
                        Connect with Lake Stone Enterprise for elite credit building and business consulting. Secure the funding your vision deserves.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
                    {}
                    <Card className="border-primary/30 bg-card/40 backdrop-blur-md shadow-2xl shadow-primary/5 relative overflow-hidden group rounded-lg">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        <CardHeader className="text-center pb-2">
                            <div className="mx-auto bg-primary/10 p-4 rounded-lg mb-4 border border-primary/20">
                                <PhoneCall className="size-12 text-primary" />
                            </div>
                            <CardTitle className="text-2xl font-default uppercase tracking-tight">Direct Line</CardTitle>
                            <CardDescription className="text-base text-muted-foreground">Priority Financial Support</CardDescription>
                        </CardHeader>
                        <CardContent className="text-center pb-6">
                            <p className="text-2xl font-mono font-bold text-foreground tracking-tighter">{phone}</p>
                        </CardContent>
                        <CardFooter className="justify-center pt-0">
                            <Button variant="default" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-md" onClick={() => handleCopy(phone, 'phone')}>
                                {copiedPhone ? <Check className="mr-2 size-4" /> : <Copy className="mr-2 size-4" />}
                                {copiedPhone ? 'Copied!' : 'Copy Number'}
                            </Button>
                        </CardFooter>
                    </Card>

                    {}
                    <Card className="border-border/50 bg-card/40 backdrop-blur-md hover:border-primary/30 transition-all duration-300 rounded-lg">
                        <CardHeader className="text-center pb-2">
                            <div className="mx-auto bg-secondary p-4 rounded-lg mb-4 border border-border/50">
                                <Mail className="size-12 text-primary" />
                            </div>
                            <CardTitle className="text-2xl font-default uppercase tracking-tight">Email Desk</CardTitle>
                            <CardDescription className="text-base text-muted-foreground">Strategy & Documentation</CardDescription>
                        </CardHeader>
                        <CardContent className="text-center pb-6">
                            <p className="text-lg font-medium text-foreground truncate px-2 font-mono">{email}</p>
                        </CardContent>
                        <CardFooter className="justify-center pt-0">
                            <Button variant="outline" className="w-full border-primary/50 text-primary hover:bg-primary/10 rounded-md" onClick={() => handleCopy(email, 'email')}>
                                {copiedEmail ? <Check className="mr-2 size-4" /> : <Copy className="mr-2 size-4" />}
                                {copiedEmail ? 'Copied!' : 'Copy Email'}
                            </Button>
                        </CardFooter>
                    </Card>

                    {}
                    <Card className="border-border/50 bg-card/40 backdrop-blur-md hover:border-primary/30 transition-all duration-300 rounded-lg">
                        <CardHeader className="text-center pb-2">
                            <div className="mx-auto bg-secondary p-4 rounded-lg mb-4 border border-border/50">
                                <MessageSquare className="size-12 text-primary" />
                            </div>
                            <CardTitle className="text-2xl font-default uppercase tracking-tight">Consultation</CardTitle>
                            <CardDescription className="text-base text-muted-foreground">Speak with a Strategist</CardDescription>
                        </CardHeader>
                        <CardContent className="text-center pb-6">
                            <p className="text-lg font-medium text-muted-foreground font-default">Response within 24 hours</p>
                        </CardContent>
                        <CardFooter className="justify-center pt-0">
                            <Button variant="secondary" className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-md">
                                <MessageSquare className="mr-2 size-4" />
                                Book Session
                            </Button>
                        </CardFooter>
                    </Card>
                </div>

                {}
                <div className="max-w-4xl mx-auto bg-card/60 backdrop-blur-xl border border-primary/20 rounded-lg p-6 md:p-10 shadow-4xl">
                    <div className="mb-8">
                        <h3 className="text-3xl font-bold font-default mb-2 text-foreground">Strategic Inquiry Form</h3>
                        <p className="text-muted-foreground font-default">Submit your details for a comprehensive credit and business analysis from our senior consultants.</p>
                    </div>
                    
                    <FormProvider formKey="reach_out_expert_form_contact" formId="reach_out_expert_form_contact" sectionName="Direct Support" defaultValues={{
          name: "",
          phone: "",
          email: "",
          vehicle_reg: "",
          location: "",
          issue: "",
          preferred_contact: "Phone"
        }} validationRules={{
          name: {
            required: true
          },
          phone: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          vehicle_reg: {
            required: true
          },
          location: {
            required: true
          },
          issue: {
            required: true
          },
          preferred_contact: {
            required: true
          }
        }} submitText="Send Message" successMessage="Your inquiry has been received. A Lake Stone strategist will contact you shortly.">
                        <SupportForm />
                    </FormProvider>
                </div>
            </div>
        </section>;
}